export interface PreviewMapping {
  featureFlag: string;
  previewFlag: string;
  basePath: string;
  contentKey: string;
  upgradeType: string;
  // Set when the pages below basePath have their own flags and previews.
  exactPathOnly?: boolean;
}

export type PreviewBullet = string | { label: string; bullets: string[] };

export interface PreviewContent {
  header?: string;
  title: string;
  subtitle: string;
  bullets: PreviewBullet[];
  image: string;
  footer?: string;
}

export interface GenericPreviewResult {
  shouldShowPreview: boolean;
  contentKey?: string;
  upgradeType?: string;
}

// Preview page mappings configuration
export const PREVIEW_MAPPINGS: PreviewMapping[] = [
  {
    featureFlag: "markets-carbon-au",
    previewFlag: "markets-carbon-au-preview",
    basePath: "/markets/carbon/australia",
    contentKey: "carbon-australia",
    upgradeType: "Market access",
  },
  {
    featureFlag: "markets-carbon-int",
    previewFlag: "markets-carbon-int-preview",
    basePath: "/markets/carbon/international",
    contentKey: "carbon-international",
    upgradeType: "Market access",
  },
  {
    featureFlag: "markets-environmental-au",
    previewFlag: "markets-environmental-au-preview",
    basePath: "/markets/environmental/australia",
    contentKey: "environmental-australia",
    upgradeType: "Market access",
  },
  {
    featureFlag: "markets-power-au",
    previewFlag: "markets-power-au-preview",
    basePath: "/markets/power/australia",
    contentKey: "power-australia",
    upgradeType: "Market access",
  },
  {
    featureFlag: "asset-intelligence-portfolios",
    previewFlag: "asset-intelligence-portfolios-preview",
    basePath: "/asset-intelligence/portfolios",
    contentKey: "project-marketplace-portfolios",
    upgradeType: "Market access",
  },
  {
    featureFlag: "asset-intelligence-market-dynamics",
    previewFlag: "asset-intelligence-market-dynamics-preview",
    basePath: "/asset-intelligence/market-dynamics",
    contentKey: "asset-intelligence-market-dynamics",
    upgradeType: "Market access",
  },
  {
    featureFlag: "asset-intelligence-scenario-analysis",
    previewFlag: "asset-intelligence-scenario-analysis-preview",
    basePath: "/asset-intelligence/scenario-analysis",
    contentKey: "project-marketplace-scenario-analysis",
    upgradeType: "Market access",
  },
  {
    featureFlag: "asset-intelligence-explorer",
    previewFlag: "asset-intelligence-explorer-preview",
    basePath: "/asset-intelligence/explorer",
    contentKey: "asset-intelligence-explorer",
    upgradeType: "Market access",
  },
  {
    featureFlag: "asset-intelligence-planning-timeline",
    previewFlag: "asset-intelligence-planning-timeline-preview",
    basePath: "/asset-intelligence/planning-timeline",
    contentKey: "asset-intelligence-planning-timeline",
    upgradeType: "Market access",
  },
  {
    featureFlag: "asset-intelligence",
    previewFlag: "asset-intelligence-preview",
    basePath: "/asset-intelligence",
    contentKey: "project-marketplace",
    upgradeType: "Market access",
    exactPathOnly: true,
  },
  {
    featureFlag: "benchmarks",
    previewFlag: "benchmarks-preview",
    basePath: "/markets/daily-curves",
    contentKey: "benchmarks",
    upgradeType: "Benchmarks",
  },
  // Each market is gated on the flag that is actually bought. The section flag
  // `benchmarks` cannot be used here: owning any one market grants it
  // automatically, which would let a preview user through to every other market.
  {
    featureFlag: "benchmarks-carbon",
    previewFlag: "benchmarks-preview",
    basePath: "/markets/benchmarks/carbon",
    contentKey: "benchmarks",
    upgradeType: "Benchmarks",
  },
  {
    featureFlag: "benchmarks-environmental",
    previewFlag: "benchmarks-preview",
    basePath: "/markets/benchmarks/environmental",
    contentKey: "benchmarks",
    upgradeType: "Benchmarks",
  },
  {
    featureFlag: "benchmarks-power",
    previewFlag: "benchmarks-preview",
    basePath: "/markets/benchmarks/power",
    contentKey: "benchmarks",
    upgradeType: "Benchmarks",
  },
  {
    featureFlag: "benchmarks-implied-volatility-curves",
    previewFlag: "benchmarks-preview",
    basePath: "/markets/benchmarks/implied-volatility-curves",
    contentKey: "benchmarks",
    upgradeType: "Benchmarks",
  },
  // Must stay after the per-market entries: the first matching mapping wins, and
  // this one also matches every /markets/benchmarks/... path. It covers the
  // section landing page for a company holding only the preview flag.
  {
    featureFlag: "benchmarks",
    previewFlag: "benchmarks-preview",
    basePath: "/markets/benchmarks",
    contentKey: "benchmarks",
    upgradeType: "Benchmarks",
  },
  {
    featureFlag: "markets-snapshot",
    previewFlag: "markets-snapshot-preview",
    basePath: "/markets/markets-snapshot",
    contentKey: "markets-snapshot",
    upgradeType: "Markets snapshot",
  },
  {
    featureFlag: "trade-timeline",
    previewFlag: "trade-timeline-preview",
    basePath: "/markets/trade-timeline",
    contentKey: "trade-timeline",
    upgradeType: "Trade timeline",
  },
];

// Preview content registry
export const PREVIEW_CONTENT_REGISTRY: Record<string, PreviewContent> = {
  "carbon-australia": {
    title: "Live insights to the Australian Carbon market",
    subtitle: "Understand what's happening, when it happens.",
    bullets: [
      "Access the market overview where live prices, trade and curve data is presented along side related market analysis and reports",
      "Keep up to date with live pricing and market commentary across ACCU and SMC products",
      "Explore historical trade data",
      "Explore ACCU project due diligence information and method premium pricing",
      "Access market reports published by our expert team",
    ],
    image: "markets-carbon-au-preview.png",
  },
  "carbon-international": {
    title: "Live insights to the International Carbon market",
    subtitle: "Understand what's happening, when it happens.",
    bullets: [
      "Access the market overview where live prices, trade and curve data is presented along side related market analysis and reports",
      "Keep up to date with pricing and market commentary across VCU, GS and CER certificates by method",
      "Explore historical trade data",
      "Explore International Carbon projects",
      "Access market reports published by our expert team",
    ],
    image: "markets-carbon-int-preview.png",
  },
  "environmental-australia": {
    title: "Live insights to the Australian Environmental market",
    subtitle: "Understand what's happening, when it happens.",
    bullets: [
      "Access the market overview where live prices, trade and curve data is present along side related market analysis and reports",
      "Keep up to date with live pricing and market commentary across LGC, STC, ESC, VEEC, and PRC products with REGOs coming soon",
      "Explore historical trade data",
      "Explore LGC and STC registry holdings data",
      "Access market reports published by our expert team",
    ],
    image: "markets-enviro-au-preview.png",
  },
  "power-australia": {
    title: "Live insights to the Australian Power market",
    subtitle: "Understand what's happening, when it happens.",
    bullets: [
      "Access the market overview where live prices, trade and curve data is present along side related market analysis and reports",
      "Keep up to date with live data and information feeds from AEMO",
      "Explore historical power shapes and ASX futures trade data",
      "Access live prices for Hourly Shapes products",
      "Access market reports published by our expert team",
    ],
    image: "markets-power-au-preview.png",
  },
  "project-marketplace": {
    header: "Asset Intelligence",
    title:
      "A comprehensive intelligence platform for the Australian power market",
    subtitle: "Key feature overview:",
    bullets: [
      {
        label: "Explorer",
        bullets: [
          "Comprehensive asset data aggregation providing complete lifecycle tracking of renewable assets, with a range of filters and supplementary GIS overlays",
        ],
      },
      {
        label: "Asset Information",
        bullets: [
          "Detailed information of each asset across their lifecycle, including; planning progression, operational and commercial performance",
        ],
      },
      {
        label: "Market Dynamics",
        bullets: [
          "Detailed quarterly commentary, pricing and behavioural insights across Offtake, Wholesale and OTC markets - includes deep dives into related topics such as; Government policy, Contracting insights and more",
        ],
      },
      {
        label: "Market and Offtake Trackers",
        bullets: [
          "Dashboards providing intelligence across current market performance and offtake benchmarks.",
        ],
      },
      {
        label: "Asset portfolios and analysis",
        bullets: [
          "View the performance for a portfolio of assets with various market scenarios to understand how they stack up against each other or how they are performing on an aggregate basis",
        ],
      },
    ],
    image: "project-explorer-preview.png",
  },
  "asset-intelligence-explorer": {
    header: "Explorer",
    title: "Comprehensive asset intelligence",
    subtitle:
      "Bringing together detailed developmental, operational and commercial insights for power and environmental assets",
    bullets: [
      "Deep dive into Explorer’s broad data set with information across all major asset lifecycle stages",
      "Supported by an array of infrastructure and GIS layers & filters to create a customised view",
      "Gain insights into new asset opportunities with in-depth planning and asset development data",
      "Understand how operational assets are performance with our rolling 12-month summary or customise your view for any period of interest",
      "Overlay current market dynamics including wholesale market pricing and our expert PPA analysis and commentary all with Explorer",
    ],
    image: "project-explorer-preview.png",
  },
  benchmarks: {
    title: "Market insights and benchmarks",
    subtitle: "Mark your position against our market-leading price benchmarks",
    bullets: [
      "Daily and quarterly market commentary provided by our in-market broker and advisory experts",
      "End-of-day benchmarks for carbon, environmental and power markets, including:",
      "Market leading ACCU generic and method-specific benchmarks for monthly and calendar year contracts",
      "SMC and selected international carbon products",
      "Environmental certificates including LGC, REGO, STC, ESC, PRC and VEECs",
      "A range of power contract benchmarks including ASX Futures, Fixed Shapes, Virtual Batteries and Hourly Power Shapes",
      "Weekly Volatility Curves for selected products across carbon, environmental and power markets",
      "API access available for integration with your internal trading, risk and settlement systems",
    ],
    image: "daily-curves-preview.png",
  },
  "markets-snapshot": {
    title: "Live snapshot of market prices",
    subtitle:
      "Stay up to date with live prices for key environmental market products",
    bullets: [
      "Live environmental prices view, as seen on Bloomberg and Reuters",
      "Real-time prices from the CORE Markets broker team",
    ],
    image: "markets-snapshot-preview.png",
  },
  "trade-timeline": {
    title: "Complete view of trades across markets",
    subtitle: "Filter and export historical trade data from our market archive",
    bullets: [
      "Search across trades from carbon environmental and power markets",
      "Filter trades by attributes including product types, trade types, method, project and more",
      "Export to CSV to run your own analysis in your preferred tools",
      "Trade timeline API is available for integration with your systems",
    ],
    image: "trade-timeline-preview.png",
  },
  "project-marketplace-portfolios": {
    header: "Portfolios",
    title: "Create & share asset collections with your team",
    subtitle:
      "Create your own asset segmentations and build comparative or aggregated analysis views",
    bullets: [
      "Group assets of interest - your own assets, those of your competitors or any other segmentation - to explore for deeper commercial and operational analysis",
      "Add and remove assets to your portfolios, or create new portfolios from across Asset Intelligence",
      "Portfolios are shared across your company to enable a collaborative space for your team",
      "Make use of your portfolios within the Scenario Analysis functionality (coming soon) that provides you with commercial analysis against customisable market dynamics.",
    ],
    image: "project-portfolio-preview.png",
  },
  "asset-intelligence-market-dynamics": {
    header: "Market Dynamics",
    title: "Your asset market data companion",
    subtitle:
      "Understand market dynamics and gain insights into asset delivery and performance across Australia",
    bullets: [
      "Keep up to date with wholesale market performance",
      "Keep track of asset performance with normalised rankings based on wholesale revenue potential and output",
      "Stay informed with regularly published PPA pricing benchmarks and our comprehensive PPA contract tracker",
      "Explore how assets are progressing through the development process and where hold ups may be occurring",
    ],
    image: "market-dynamics-preview.png",
  },
  "project-marketplace-scenario-analysis": {
    header: "Scenario Analysis",
    title: "Commercial insights with real market data",
    subtitle: "Create your own views for comparative or aggregated analysis",
    bullets: [
      "Group assets of interest - your own assets, those of your competitors or any other segmentation - to explore for deeper commercial and operational analysis",
      "Overlay customisable pricing scenarios to see how changing behaviours could make an impact to your asset or portfolio view ",
      "Save your asset views into portfolios that are shared within your company for convenient access across your team",
    ],
    image: "scenario-analysis-preview.png",
  },
};

/**
 * Generic preview logic that works with any module
 * Checks if current URL matches any preview configuration and user has required feature flag
 */
export const getGenericPreviewLogic = (
  pathname: string,
  features: string[]
): GenericPreviewResult => {
  for (const mapping of PREVIEW_MAPPINGS) {
    // Check if user has the required feature flag
    if (!features.includes(mapping.previewFlag)) continue;

    const isMatch =
      pathname === mapping.basePath ||
      (!mapping.exactPathOnly && pathname.startsWith(mapping.basePath + "/"));

    if (isMatch) {
      if (features.includes(mapping.featureFlag)) {
        return { shouldShowPreview: false };
      }
      return {
        shouldShowPreview: true,
        contentKey: mapping.contentKey,
        upgradeType: mapping.upgradeType,
      };
    }
  }

  return { shouldShowPreview: false };
};

// A section's own flag is granted alongside any page in it, so holding it does
// not prove the company owns a page. With no pages to list, preview instead.
export const getSectionPreviewLogic = (
  pathname: string,
  features: string[],
  hasAccessiblePages: boolean
): GenericPreviewResult => {
  const result = getGenericPreviewLogic(pathname, features);
  if (result.shouldShowPreview || hasAccessiblePages) return result;

  const mapping = PREVIEW_MAPPINGS.find(
    ({ basePath, previewFlag }) =>
      basePath === pathname && features.includes(previewFlag)
  );
  return mapping
    ? {
        shouldShowPreview: true,
        contentKey: mapping.contentKey,
        upgradeType: mapping.upgradeType,
      }
    : result;
};
